import { t } from "../i18n/index.ts";
import type { CommandResult, GameAction, GameCommand } from "./types.ts";

function commandId(): string {
  // getRandomValues also covers the oldest browser in the iOS 15 target range.
  return Array.from(crypto.getRandomValues(new Uint8Array(16)), b => b.toString(16).padStart(2, "0")).join("");
}

/** One pending mutation per tab/account/game. Its original ID, action and
 * expected revision survive reload and never change when a new snapshot arrives. */
export class PendingGameCommand {
  pending?: GameCommand;
  revision?: number;
  private key: string;

  constructor(account: string, game: string, private storage: Pick<Storage, "getItem" | "setItem" | "removeItem">,
    private newId: () => string = commandId) {
    this.key = `chessme.command.v2:${account}:${game}`;
    const saved = storage.getItem(this.key);
    if (!saved) return;
    // Corrupt local state is not a reusable mutation. A fresh authoritative
    // revision still fences any older command that could be in flight.
    try {
      if (saved.length > 4096) throw new Error("oversize");
      const p = JSON.parse(saved) as GameCommand;
      if (typeof p.command_id !== "string" || !/^[a-zA-Z0-9_-]{16,64}$/.test(p.command_id)
        || !Number.isSafeInteger(p.expected_revision) || p.expected_revision < 0 || !p.action
        || !["move", "resign", "draw_yes", "draw_no", "flag", "claim_win", "pause", "resume_ready", "withdraw_resume", "rematch_ready", "withdraw_rematch"].includes(p.action.type)) throw new Error("invalid");
      this.pending = p;
    } catch { storage.removeItem(this.key); }
  }

  observe(revision: number): void {
    if (Number.isSafeInteger(revision) && revision >= 0) this.revision = Math.max(this.revision ?? 0, revision);
  }

  begin(action: GameAction): GameCommand | undefined {
    if (this.pending || this.revision === undefined) return undefined;
    const command: GameCommand = { command_id: this.newId(), expected_revision: this.revision, action };
    // Persist before first send. If storage fails, no command is sent.
    this.storage.setItem(this.key, JSON.stringify(command));
    this.pending = command;
    return command;
  }

  resolve(result: CommandResult): boolean {
    if (result.command_id !== this.pending?.command_id || result.outcome === "unknown") return false;
    this.observe(result.state_revision);
    this.storage.removeItem(this.key);
    this.pending = undefined;
    return true;
  }
}

export function commandMessage(code?: string): string {
  switch (code) {
    case "stale_revision": return t("command.stale");
    case "illegal_move": case "invalid_move": return t("command.illegal");
    case "capacity_full": return t("command.capacity");
    case "player_busy": return t("command.busy");
    case "admissions_closed": return t("command.closed");
    case "rematch_exists": return t("command.rematch");
    case "new_time_control_required": return t("command.timeControl");
    case "already_ready": return t("command.waiting");
    case "game_finished": return t("command.finished");
    case "game_paused": return t("command.paused");
    case "forbidden": return t("command.forbidden");
    case "not_your_turn": return t("command.turn");
    case "not_flagged": return t("command.time");
    case "not_claimable": return t("command.claim");
    case "command_rate_limited": return t("command.wait");
    case "already_offered": return t("command.drawPending");
    case "no_draw_offer": return t("command.noDraw");
    default: return t("command.failed");
  }
}
